import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, MapPin, Phone, FileBadge, Clock, Paperclip, X, Loader2, ShieldCheck } from "lucide-react";
import { IMAGES, COMPANY, API_URL, SERVICES } from "../data/site";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/Reveal";

const inputCls =
  "w-full border border-white/15 bg-obsidian px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-blueprint";

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_EXT = [".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png", ".webp"];

const TIMELINES = [
  "Immediate (1–2 weeks)",
  "1–3 months",
  "3–6 months",
  "Budgetary / future RFP",
];

const INFO = [
  { icon: MapPin, label: "Headquarters", value: COMPANY.address, testId: "bid-address" },
  { icon: Phone, label: "Office", value: COMPANY.phone, href: COMPANY.phoneHref, testId: "bid-phone" },
  { icon: FileBadge, label: "License", value: COMPANY.license, testId: "bid-license" },
  { icon: Clock, label: "Bidding & Payroll", value: "All handled at the Paso Robles HQ", testId: "bid-office-note" },
];

export default function RequestBid() {
  const [params] = useSearchParams();
  const preset = params.get("service") || "";
  const [form, setForm] = useState({
    fullName: "",
    company: "",
    email: "",
    phone: "",
    serviceType: preset,
    projectLocation: "",
    timeline: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [scanStatus, setScanStatus] = useState(null);
  const [attachment, setAttachment] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pollScan = async (uploadId) => {
    for (let i = 0; i < 30; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const res = await axios.get(`${API_URL}/uploads/${uploadId}/status`);
        if (res.data.status === "clean") {
          setAttachment({ path: res.data.path, filename: res.data.filename });
          setScanStatus("clean");
          return;
        }
        if (res.data.status === "rejected") {
          setScanStatus("rejected");
          setFileError(res.data.detail);
          setFile(null);
          return;
        }
      } catch (err) {
        setScanStatus("rejected");
        setFileError(err?.response?.data?.detail || "Virus scan failed. Please try a different file.");
        setFile(null);
        return;
      }
    }
    setScanStatus("rejected");
    setFileError("Virus scan is taking longer than expected. Please try a different file.");
    setFile(null);
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = "." + f.name.split(".").pop().toLowerCase();
    if (!ACCEPTED_EXT.includes(ext)) {
      setFileError("Unsupported file type. Use PDF, DOC, DOCX, JPG, PNG or WEBP.");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setFileError("File too large. Max size is 10MB.");
      e.target.value = "";
      return;
    }
    setFileError("");
    setFile(f);
    setAttachment(null);
    setScanStatus("scanning");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const up = await axios.post(`${API_URL}/uploads`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      pollScan(up.data.uploadId);
    } catch (err) {
      setScanStatus("rejected");
      setFileError(err?.response?.data?.detail || "Upload failed. Please try again.");
      setFile(null);
    }
  };

  const removeFile = () => {
    setFile(null);
    setFileError("");
    setScanStatus(null);
    setAttachment(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (scanStatus === "scanning") {
      toast.error("Please wait for the virus scan to finish.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/enquiries`, {
        ...form,
        formType: "bid",
        attachmentPath: attachment?.path || null,
        attachmentName: attachment?.filename || null,
      });
      setDone(true);
      toast.success("Bid request sent — our estimating office has it.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please call our office instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        testId="bid-hero"
        eyebrow="Request a Bid // Paso Robles HQ"
        lines={["Let's scope", "your crossing."]}
        sub="Large project proposals and day-to-day time-and-material work. Send the details — all bidding is handled directly at our headquarters."
        image={IMAGES.heroAerial}
      />

      <section data-testid="bid-main" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-16">
            <div>
              <Reveal>
                <Eyebrow>Direct Lines</Eyebrow>
                <h2 className="mt-4 font-display text-4xl font-extrabold uppercase tracking-tight text-white">
                  {COMPANY.name}
                </h2>
              </Reveal>
              <div className="mt-8 grid gap-px border border-white/10 bg-white/10">
                {INFO.map((item, i) => (
                  <Reveal key={item.label} delay={i * 0.05}>
                    <div className="flex items-start gap-4 bg-obsidian p-5">
                      <item.icon className="mt-0.5 h-5 w-5 shrink-0 text-brand-light" />
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                          {item.label}
                        </p>
                        {item.href ? (
                          <a href={item.href} data-testid={item.testId} className="mt-1 block text-sm text-blueprint hover:text-white">
                            {item.value}
                          </a>
                        ) : (
                          <p data-testid={item.testId} className="mt-1 text-sm text-slate-200">{item.value}</p>
                        )}
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            <Reveal delay={0.1}>
              {done ? (
                <div
                  data-testid="bid-success"
                  className="blueprint-grid flex h-full min-h-[24rem] flex-col items-center justify-center border border-blueprint/30 bg-obsidian p-10 text-center"
                >
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-blueprint">
                    Bid Request Received
                  </p>
                  <p className="mt-4 font-display text-4xl font-extrabold uppercase text-white">
                    It's on the estimator's desk.
                  </p>
                  <p className="mt-3 max-w-sm text-sm text-slate-400">
                    We've logged your bid request and notified the office. For urgent work, call{" "}
                    <a href={COMPANY.phoneHref} className="text-blueprint">{COMPANY.phone}</a>.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="bid-form" className="border border-white/10 bg-granite/60 p-6 sm:p-8">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="b-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Full Name *</label>
                      <input id="b-name" data-testid="bid-input-name" required value={form.fullName} onChange={set("fullName")} className={inputCls} placeholder="Jane Doe" />
                    </div>
                    <div>
                      <label htmlFor="b-company" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Company / Agency</label>
                      <input id="b-company" data-testid="bid-input-company" value={form.company} onChange={set("company")} className={inputCls} placeholder="Agency or GC" />
                    </div>
                    <div>
                      <label htmlFor="b-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Email *</label>
                      <input id="b-email" type="email" data-testid="bid-input-email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" />
                    </div>
                    <div>
                      <label htmlFor="b-phone" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Phone</label>
                      <input id="b-phone" type="tel" data-testid="bid-input-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(805) 555-0100" />
                    </div>
                    <div>
                      <label htmlFor="b-service" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Capability Needed *</label>
                      <select id="b-service" data-testid="bid-select-service" required value={form.serviceType} onChange={set("serviceType")} className={inputCls}>
                        <option value="" disabled>Select a capability</option>
                        {SERVICES.map((s) => (
                          <option key={s.id} value={s.title}>{s.title}</option>
                        ))}
                        <option value="General / Other">General / Other</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="b-location" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Project Location (CA) *</label>
                      <input id="b-location" data-testid="bid-input-location" required value={form.projectLocation} onChange={set("projectLocation")} className={inputCls} placeholder="City / County" />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="b-timeline" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Estimated Start</label>
                      <select id="b-timeline" data-testid="bid-select-timeline" value={form.timeline} onChange={set("timeline")} className={inputCls}>
                        <option value="">Select a timeline</option>
                        {TIMELINES.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="b-message" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Project Scope / RFP Details *</label>
                      <textarea id="b-message" rows={5} data-testid="bid-input-message" required value={form.message} onChange={set("message")} className={inputCls} placeholder="Bore lengths, pipe diameters, soil conditions, crossings…" />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="b-file" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Attach Plans / Photos (optional)</label>
                      {!file ? (
                        <label
                          htmlFor="b-file"
                          data-testid="bid-file-dropzone"
                          className="flex cursor-pointer items-center gap-3 border border-dashed border-white/15 bg-obsidian px-4 py-3 text-sm text-slate-400 transition-colors hover:border-blueprint hover:text-white"
                        >
                          <Paperclip className="h-4 w-4 shrink-0 text-brand-light" />
                          PDF, DOC, JPG, PNG or WEBP — up to 10MB
                        </label>
                      ) : (
                        <div
                          data-testid="bid-file-selected"
                          className="flex items-center justify-between gap-3 border border-white/15 bg-obsidian px-4 py-3 text-sm text-slate-200"
                        >
                          <span className="flex items-center gap-2 truncate">
                            {scanStatus === "scanning" ? (
                              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blueprint" />
                            ) : (
                              <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-400" />
                            )}
                            <span className="truncate">{file.name}</span>
                            {scanStatus === "scanning" && (
                              <span data-testid="bid-file-scanning" className="shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                Scanning…
                              </span>
                            )}
                          </span>
                          <button
                            type="button"
                            onClick={removeFile}
                            data-testid="bid-file-remove-button"
                            aria-label="Remove attachment"
                            className="shrink-0 text-slate-500 transition-colors hover:text-white"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                      <input
                        id="b-file"
                        type="file"
                        data-testid="bid-file-input"
                        accept={ACCEPTED_EXT.join(",")}
                        onChange={onFileChange}
                        className="hidden"
                      />
                      {fileError && (
                        <p data-testid="bid-file-error" className="mt-2 text-xs text-red-400">{fileError}</p>
                      )}
                    </div>
                  </div>
                  <button
                    type="submit"
                    data-testid="bid-submit-button"
                    disabled={submitting || scanStatus === "scanning"}
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-brand px-6 py-4 text-sm font-semibold text-white transition-colors hover:bg-brand-light disabled:opacity-50"
                  >
                    {submitting ? "Sending…" : scanStatus === "scanning" ? "Scanning attachment…" : "Send Bid Request"}
                    <ArrowUpRight className="h-4 w-4" />
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
